import { STATUS } from "./model.js";

/**
 * @param {{editing?: boolean, awaitingChoice?: boolean, paused?: boolean}} pp  panel state
 * @param {{applyPending?: boolean, pendingInvalid?: boolean}} date  the resolution picker's
 *   typed date: `applyPending` when a changed valid date is waiting to be applied,
 *   `pendingInvalid` when a changed date does not parse
 * @returns {{visible: boolean, countdown: boolean, disabled: boolean, escapeTarget: boolean}}
 */
export function continueState(pp, { applyPending = false, pendingInvalid = false } = {}) {
  // Hidden while the question text is being edited (SUBMIT EDIT owns that), and while the
  // forecast is held for a resolution choice, unless a typed date is ready to apply.
  const visible = !pp.editing && (!pp.awaitingChoice || applyPending);
  const datePending = applyPending || pendingInvalid;
  return {
    visible,
    // Running out confirms the panel, so it must never run past a date the user typed.
    countdown: visible && !pp.paused && !datePending,
    disabled: pendingInvalid,
    // Escape clicks the popup's cancel target; with a date pending that would apply it.
    escapeTarget: visible && !datePending,
  };
}

/**
 * The resolution moment a question shows. The panel keys its date picker on it, so the picker
 * remounts, with its date editor closed, whenever the moment changes.
 *
 * @param {{custom_metadata?: {resolution_moment_utc?: string}}|null|undefined} question
 * @returns {string}
 */
export function resolutionMomentKey(question) {
  return question?.custom_metadata?.resolution_moment_utc || "";
}

/**
 * Whether the panel closes because its own run completed: once the forecast is in, confirming
 * starts nothing. A panel whose question or resolution date the user is editing stays open, so
 * their input is never thrown away. `editingDate` holds the moment the date editor was opened
 * on, and counts only while the question still shows that moment: a new moment remounts the
 * picker with its editor closed.
 *
 * @param {{editing?: boolean, editingDate?: string|null, question?: object}|null} pp  panel state
 * @param {string|null|undefined} runStatus  the status of the run the panel belongs to
 * @returns {boolean}
 */
export function closesOnCompletion(pp, runStatus) {
  if (!pp || runStatus !== STATUS.COMPLETED || pp.editing) return false;
  return pp.editingDate == null || pp.editingDate !== resolutionMomentKey(pp.question);
}
